if (!customElements.get("color-swatch")) {
  class ColorSwatch extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.onClick.bind(this);
      this.handleTrackingClick = this.onTrackingClick.bind(this);
      this.handleMouseEnter = this.onMouseEnter.bind(this);
      this.carouselObserver = null;
      this.preloadObserver = null;
      this.preloadIdleCallback = null;
      this.preloadIdleCallbackType = null;
      ColorSwatch.productCache = ColorSwatch.productCache || new Map();
      ColorSwatch.productRequests = ColorSwatch.productRequests || new Map();
      ColorSwatch.swatchCache = ColorSwatch.swatchCache || new Map();
      ColorSwatch.swatchRequests = ColorSwatch.swatchRequests || new Map();
      ColorSwatch.swatchQueue = ColorSwatch.swatchQueue || [];
      ColorSwatch.activeSwatchRequests =
        ColorSwatch.activeSwatchRequests || 0;
      ColorSwatch.maxConcurrentSwatchRequests = 4;
      this.productCache = ColorSwatch.productCache;
      this.productRequests = ColorSwatch.productRequests;
      this.swatchCache = ColorSwatch.swatchCache;
      this.swatchRequests = ColorSwatch.swatchRequests;
      this.maxProductCacheSize = 80;
    }

    connectedCallback() {
      this.optionNodes = this.querySelectorAll("[data-value]");
      this.productCard = this.closest(".product-card");
      this.initialProductUrl =
        this.productCard
          ?.querySelector(".full-width-link.product-card__link")
          ?.getAttribute("href") || "";
      this.currentProductUrl = this.initialProductUrl;
      this.mainImage = this.productCard?.querySelector(
        ".product-card__image--main img"
      );
      this.placeholderTemplate = this.productCard?.querySelector(
        "[data-product-card-placeholder]"
      );
      this.sliderEnabled = this.dataset.plpColourSlider === "true";

      if (this.sliderEnabled) {
        this.getColorMap();
        this.initClickSwatches();
        this.syncSelectedColour();
        this.observeSelectedColour();
        this.initPreloadObserver();
      } else {
        this.getVariantData();
        this.initOptionSwatches();
        this.initHoverSwatches();
      }
    }

    disconnectedCallback() {
      this.optionNodes.forEach((button) => {
        button.removeEventListener("click", this.handleClick);
        button.removeEventListener("click", this.handleTrackingClick);
        button.removeEventListener("mouseenter", this.handleMouseEnter);
      });

      if (this.carouselObserver) {
        this.carouselObserver.disconnect();
        this.carouselObserver = null;
      }

      if (this.preloadObserver) {
        this.preloadObserver.disconnect();
        this.preloadObserver = null;
      }

      if (this.preloadIdleCallback !== null) {
        if (this.preloadIdleCallbackType === "idle") {
          window.cancelIdleCallback(this.preloadIdleCallback);
        } else {
          window.clearTimeout(this.preloadIdleCallback);
        }
        this.preloadIdleCallback = null;
        this.preloadIdleCallbackType = null;
      }
    }

    initHoverSwatches() {
      this.optionNodes.forEach((button) => {
        button.addEventListener("mouseenter", this.handleMouseEnter);
        button.addEventListener("click", this.handleTrackingClick);
      });
      this.selected = this.querySelector(this.getSelectedSwatchSelector());
    }

    initClickSwatches() {
      this.optionNodes.forEach((button) =>
        button.addEventListener("click", this.handleClick)
      );

      this.selected = this.getInitialSelectedSwatch();
      this.optionNodes.forEach((swatch) => this.setSwatchSelected(swatch, false));

      if (this.selected) {
        this.setSwatchSelected(this.selected, true);
      }
    }

    getInitialSelectedSwatch() {
      const initialProductPath = this.getPathFromUrl(this.initialProductUrl);
      const swatches = Array.from(this.optionNodes);

      if (initialProductPath) {
        return (
          swatches.find(
            (swatch) =>
              this.getPathFromUrl(
                swatch.dataset.swatchUrl || swatch.getAttribute("href")
              ) ===
              initialProductPath
          ) || null
        );
      }

      return this.querySelector(`${this.getSelectedSwatchSelector()}[data-value]`);
    }

    getSelectedStateAttribute() {
      return this.sliderEnabled ? "aria-pressed" : "aria-selected";
    }

    getSelectedSwatchSelector() {
      return `[${this.getSelectedStateAttribute()}="true"]`;
    }

    setSwatchSelected(swatch, selected) {
      if (!swatch) return;

      const attribute = this.getSelectedStateAttribute();
      if (selected) {
        swatch.setAttribute(attribute, "true");
      } else if (this.sliderEnabled) {
        swatch.setAttribute(attribute, "false");
      } else {
        swatch.removeAttribute(attribute);
      }
    }

    syncSelectedColour() {
      if (!this.selected || !this.colorMap) return;

      const colourData = this.colorMap?.[this.selected?.dataset?.value];
      if (!colourData) return;

      this.updateCardState(colourData);

      if (colourData.images?.length) {
        this.updateStaticImage(colourData.images[0]);
      }
    }

    initOptionSwatches() {
      this.optionNodes.forEach((optNode) => {
        let variantImage;
        const { optionPosition, value: optionValue } = optNode.dataset;
        const variant = this.variantData.find(
          (v) =>
            v[`option${optionPosition}`] &&
            v[`option${optionPosition}`].toLowerCase() ===
              optionValue.toLowerCase()
        );

        if (variant) {
          variantImage = variant.featured_image
            ? variant.featured_image.src
            : "";
          optNode.href = this.buildVariantUrl(optNode.href, variant.id);
          optNode.dataset.variantId = variant.id;

          if (variantImage) {
            optNode.setAttribute(
              "data-src",
              getSizedImageUrl(variantImage, "900x")
            );
            optNode.setAttribute("data-srcset", getSrcset(variantImage));
          }

          if (variant && variant.sku) {
            optNode.dataset.variantSku = variant.sku;
          }
        }
      });
    }

    pushColourSwatchEngagement(swatch) {
      if (!swatch || !window.ShopifyTracking) return;

      const colour = swatch.dataset.value || swatch.dataset.colour;
      const colourCode = swatch.dataset.variantSku || swatch.dataset.sku || "";
      if (!colour || !colourCode) return;

      window.ShopifyTracking.push("colour_swatch_engagement", {
        colour: colour,
        colourCode: colourCode,
        content_ids: [colourCode],
        content_type: "product_group"
      });
    }

    onMouseEnter(e) {
      const target = e.currentTarget;
      this.setSwatchSelected(this.selected, false);
      this.setSwatchSelected(target, true);

      const { src, srcset, optionPosition, value: optionValue } = target.dataset;
      if (this.mainImage && srcset) {
        this.mainImage.src = src;
        this.mainImage.srcset = srcset;
      }

      this.selected = target;

      const variant = this.variantData?.find(
        (v) =>
          (target.dataset.variantId &&
            String(v.id) === String(target.dataset.variantId)) ||
          (v[`option${optionPosition}`] &&
            optionValue &&
            v[`option${optionPosition}`].toLowerCase() ===
              optionValue.toLowerCase())
      );

      if (variant) {
        const variantImage = variant.featured_image?.src || src || "";
        this.updateWishlistButton({
          variantId: variant.id,
          url: target.getAttribute("href") || this.currentProductUrl,
          sku: variant.sku || target.dataset.variantSku || "",
          variantName: variant.name || "",
          productPrice:
            variant.price != null ? Number(variant.price) / 100 : null,
          wishlistImage: variantImage,
          colour: optionValue || "",
        });
      }
    }

    onTrackingClick(e) {
      this.pushColourSwatchEngagement(e.currentTarget);
    }

    onClick(e) {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }

      const link = e.currentTarget;
      const colourData = this.colorMap?.[link.dataset.value];
      if (!colourData) {
        if (this.sliderEnabled) {
          e.preventDefault();
        }
        return;
      }

      e.preventDefault();

      this.setSwatchSelected(this.selected, false);
      this.setSwatchSelected(link, true);
      this.selected = link;
      this.pushColourSwatchEngagement(link);

      // Combined Listings swatches may point at sibling product URLs.
      const currentProductUrl = this.getCurrentProductPath();
      const siblingProductUrl = this.getPathFromUrl(colourData.url);
      const isSiblingProduct = siblingProductUrl !== currentProductUrl;

      if (isSiblingProduct) {
        this.fetchSiblingProductData(colourData.url, link.dataset.value);
      } else {
        this.renderColourData(colourData);
      }
    }

    async fetchSiblingProductData(productUrl, colourValue) {
      try {
        const swatchPromise = this.getSiblingSwatchData(productUrl, true).catch((error) => {
          console.error("Failed to fetch sibling swatch data:", error);
          return null;
        });
        const [productData, swatchData] = await Promise.all([
          this.getSiblingProductData(productUrl),
          swatchPromise,
        ]);
        if (this.selected?.dataset.value !== colourValue) return;

        this.colorMap[colourValue] = {
          ...this.colorMap[colourValue],
          ...this.getSwatchMetadata(swatchData),
          ...productData,
        };

        const colourData = this.colorMap[colourValue];
        const swatch = Array.from(this.optionNodes).find(
          (node) => node.dataset.value === colourValue
        );
        this.updateSwatchVisual(swatch, swatchData);
        this.renderColourData(colourData);
      } catch (error) {
        console.error("Failed to fetch sibling product data:", error);
        if (this.selected?.dataset.value !== colourValue) return;

        this.restoreCommittedSelection();
      }
    }

    async getSiblingProductData(productUrl) {
      const cacheKey = this.getProductCacheKey(productUrl);
      if (this.productCache.has(cacheKey)) {
        return this.productCache.get(cacheKey);
      }

      if (this.productRequests.has(cacheKey)) {
        return this.productRequests.get(cacheKey);
      }

      const requestPromise = this.fetchAndProcessProductData(productUrl)
        .then((processedData) => {
          this.setCachedProductData(cacheKey, processedData);
          return processedData;
        })
        .finally(() => {
          this.productRequests.delete(cacheKey);
        });

      this.productRequests.set(cacheKey, requestPromise);
      return requestPromise;
    }

    async fetchAndProcessProductData(productUrl) {
      const jsUrl = `${this.getProductCacheKey(productUrl)}.js`;
      const response = await fetch(jsUrl);

      if (!response.ok) {
        throw new Error(`Product endpoint failed with ${response.status}`);
      }

      const productData = await response.json();
      const requestedVariantId = new URL(
        productUrl,
        window.location.origin
      ).searchParams.get("variant");
      const selectedVariant =
        (requestedVariantId &&
          productData.variants?.find(
            (variant) => String(variant.id) === String(requestedVariantId)
          )) ||
        productData.variants?.find((variant) => variant.available && variant.price > 0) ||
        productData.variants?.find((variant) => variant.available) ||
        productData.variants?.find((variant) => variant.price > 0) ||
        productData.variants?.[0] ||
        null;
      const images = this.processProductImages(productData.media);
      const rawWishlistImage =
        selectedVariant?.featured_image?.src ||
        selectedVariant?.image?.src ||
        (typeof productData.featured_image === "string"
          ? productData.featured_image
          : productData.featured_image?.src) ||
        images[0]?.src ||
        "";
      const result = {
        title: productData.title,
        productName: productData.type || productData.title,
        handle: productData.handle,
        price: this.formatMoney(productData.price),
        productPrice: productData.price / 100,
        hasProductPrice: productData.price > 0,
        compareAtPrice:
          productData.compare_at_price > productData.price
            ? this.formatMoney(productData.compare_at_price)
            : null,
        available: productData.available,
        images,
        trackingItem: this.buildTrackingItem(productData, selectedVariant),
      };

      if (productData.id != null) result.productId = productData.id;
      if (selectedVariant?.id != null) result.variantId = selectedVariant.id;
      if (selectedVariant?.sku) result.sku = selectedVariant.sku;
      if (selectedVariant?.name || productData.title) {
        result.variantName = selectedVariant?.name || productData.title;
      }
      if (productData.vendor) result.vendor = productData.vendor;
      if (productData.type) result.productType = productData.type;
      if (rawWishlistImage) {
        result.wishlistImage = this.getImageUrl(rawWishlistImage, 1024);
      }
      const wishlistSize = this.buildVariantSize(productData, selectedVariant);
      if (wishlistSize) result.wishlistSize = wishlistSize;

      return result;
    }

    isColourOptionName(optionName) {
      const normalized = String(optionName || "").trim().toLowerCase();
      return /^(color|colour|couleur|farbe|colore|kleur|färg|farve|farge|kolor|cor|finish)$/.test(
        normalized
      );
    }

    buildVariantSize(productData, variant) {
      if (!productData || !variant) return "";

      const optionNames = Array.isArray(productData.options)
        ? productData.options.map((option) =>
            typeof option === "string" ? option : option?.name || ""
          )
        : [];
      const variantOptions = Array.isArray(variant.options) ? variant.options : [];
      const sizeParts = [];

      optionNames.forEach((optionName, index) => {
        const value = variantOptions[index] || variant[`option${index + 1}`] || "";
        if (!value) return;
        if (!this.isColourOptionName(optionName)) {
          sizeParts.push(value);
        }
      });

      return sizeParts.join("/");
    }

    buildTrackingItem(productData, variant) {
      if (!productData || !variant) return null;

      const optionNames = Array.isArray(productData.options)
        ? productData.options.map((option) =>
            typeof option === "string" ? option : option?.name || ""
          )
        : [];
      const variantOptions = Array.isArray(variant.options) ? variant.options : [];
      let itemVariant = "";
      const productSizeParts = [];

      optionNames.forEach((optionName, index) => {
        const value = variantOptions[index] || variant[`option${index + 1}`] || "";
        if (this.isColourOptionName(optionName)) {
          itemVariant = value;
        } else {
          productSizeParts.push(value);
        }
      });

      const productSize = productSizeParts.join("").replace(/\s*\/\s*/g, "");
      const fullProductCode = String(variant.sku || "").toUpperCase();
      const normalizedSize = productSize.replace(/\s+/g, "").toUpperCase();
      const handleProductCode = String(productData.handle || "")
        .split("-")
        .filter(Boolean)
        .pop()
        ?.toUpperCase();
      const itemId =
        normalizedSize && fullProductCode.endsWith(normalizedSize)
          ? fullProductCode.slice(0, -normalizedSize.length)
          : handleProductCode;
      const price = Number(variant.price || productData.price || 0) / 100;
      const compareAtPrice =
        Number(variant.compare_at_price) > Number(variant.price)
          ? Number(variant.compare_at_price) / 100
          : price;
      const normalizedItemVariant = String(itemVariant || "")
        .trim()
        .toLowerCase()
        .replace(/(^|[\s-])([a-z])/g, (match, separator, letter) =>
          separator + letter.toUpperCase()
        );

      return {
        item_id: itemId,
        full_product_code: fullProductCode,
        item_name: productData.title,
        discount: Math.max(0, compareAtPrice - price),
        item_brand: productData.vendor,
        item_category: productData.type,
        item_variant: normalizedItemVariant,
        price,
        current_product_price: price,
        original_product_price: compareAtPrice,
        product_size: productSize,
      };
    }

    getProductCacheKey(productUrl) {
      return this.getPathFromUrl(productUrl);
    }

    getPathFromUrl(url) {
      if (!url) return "";
      return new URL(url, window.location.origin).pathname;
    }

    buildVariantUrl(url, variantId) {
      const variantUrl = new URL(url, window.location.origin);

      if (variantId) {
        variantUrl.searchParams.set("variant", variantId);
      } else {
        variantUrl.searchParams.delete("variant");
      }

      return `${variantUrl.pathname}${variantUrl.search}`;
    }

    getCurrentProductPath() {
      return this.getPathFromUrl(this.currentProductUrl);
    }

    restoreCommittedSelection() {
      const currentProductPath = this.getCurrentProductPath();
      const committedSelection = Array.from(this.optionNodes || []).find(
        (node) => {
          const colourData = this.colorMap?.[node.dataset.value];
          return (
            colourData?.url &&
            this.getPathFromUrl(colourData.url) === currentProductPath
          );
        }
      );

      this.setSwatchSelected(this.selected, false);
      this.setSwatchSelected(committedSelection, true);
      this.selected = committedSelection || null;
    }

    setCachedProductData(cacheKey, productData) {
      if (this.productCache.has(cacheKey)) {
        this.productCache.delete(cacheKey);
      }

      this.productCache.set(cacheKey, productData);

      if (this.productCache.size > this.maxProductCacheSize) {
        this.productCache.delete(this.productCache.keys().next().value);
      }
    }

    async getSiblingSwatchData(productUrl, priority = false) {
      const cacheKey = this.getProductCacheKey(productUrl);
      if (this.swatchCache.has(cacheKey)) {
        return this.swatchCache.get(cacheKey);
      }

      if (this.swatchRequests.has(cacheKey)) {
        if (priority) this.prioritizeQueuedSwatch(cacheKey);
        return this.swatchRequests.get(cacheKey);
      }

      const requestPromise = this.enqueueSwatchRequest(
        cacheKey,
        () => this.fetchSwatchData(productUrl),
        priority
      )
        .then((swatchData) => {
          this.setCachedSwatchData(cacheKey, swatchData);
          return swatchData;
        })
        .finally(() => {
          this.swatchRequests.delete(cacheKey);
        });

      this.swatchRequests.set(cacheKey, requestPromise);
      return requestPromise;
    }

    enqueueSwatchRequest(cacheKey, request, priority = false) {
      const SwatchClass = this.constructor;
      return new Promise((resolve, reject) => {
        const entry = { cacheKey, request, resolve, reject };
        if (priority) SwatchClass.swatchQueue.unshift(entry);
        else SwatchClass.swatchQueue.push(entry);
        this.processSwatchQueue();
      });
    }

    prioritizeQueuedSwatch(cacheKey) {
      const queue = this.constructor.swatchQueue;
      const index = queue.findIndex((entry) => entry.cacheKey === cacheKey);
      if (index <= 0) return;
      queue.unshift(queue.splice(index, 1)[0]);
    }

    processSwatchQueue() {
      const SwatchClass = this.constructor;
      while (
        SwatchClass.activeSwatchRequests <
          SwatchClass.maxConcurrentSwatchRequests &&
        SwatchClass.swatchQueue.length
      ) {
        const entry = SwatchClass.swatchQueue.shift();
        SwatchClass.activeSwatchRequests += 1;
        Promise.resolve()
          .then(entry.request)
          .then(entry.resolve, entry.reject)
          .finally(() => {
            SwatchClass.activeSwatchRequests -= 1;
            this.processSwatchQueue();
          });
      }
    }

    async fetchSwatchData(productUrl) {
      const sectionUrl = `${this.getProductCacheKey(productUrl)}?section_id=product-card-colour-data`;
      const response = await fetch(sectionUrl, {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Product colour data section failed with ${response.status}`
        );
      }

      const html = await response.text();
      const payload = new DOMParser()
        .parseFromString(html, "text/html")
        .querySelector("[data-product-card-colour-data]");

      if (!payload) {
        throw new Error("Product colour data section payload is missing");
      }

      try {
        const swatchData = JSON.parse(payload.textContent);
        if (
          !swatchData ||
          typeof swatchData !== "object" ||
          Array.isArray(swatchData)
        ) {
          throw new Error("Expected an object payload");
        }
        return {
          src: swatchData.src ?? null,
          focalPoint: swatchData.focalPoint ?? null,
          range: swatchData.range ?? null,
          style: swatchData.style ?? null,
          color: swatchData.color ?? null,
          title: swatchData.title ?? null,
          productName: swatchData.productName ?? null,
        };
      } catch (error) {
        throw new Error(
          `Product colour data section payload is invalid: ${error.message}`
        );
      }
    }

    getSwatchMetadata(swatchData) {
      if (!swatchData) return {};

      return Object.fromEntries(
        Object.entries({
          range: swatchData.range,
          style: swatchData.style,
          color: swatchData.color,
          title: swatchData.title,
          productName: swatchData.productName,
        }).filter(([, value]) => value != null && value !== "")
      );
    }

    setCachedSwatchData(cacheKey, swatchData) {
      if (this.swatchCache.has(cacheKey)) {
        this.swatchCache.delete(cacheKey);
      }

      this.swatchCache.set(cacheKey, swatchData);

      if (this.swatchCache.size > this.maxProductCacheSize) {
        this.swatchCache.delete(this.swatchCache.keys().next().value);
      }
    }

    formatMoney(cents) {
      if (typeof window.formatMoney === "function") {
        return window.formatMoney(cents, window.FoxThemeSettings?.money_format);
      }

      return (cents / 100).toFixed(2);
    }

    processProductImages(media) {
      if (!Array.isArray(media) || !media.length) return [];

      return media
        .filter((m) => m?.media_type === "image")
        .map((m) => {
          const image = m.image || m;
          if (!image?.src) return null;

          return {
            src: image.src,
            srcset: this.generateSrcset(image),
            sizes: "(min-width: 1200px) calc((1200px - 130px) / 4), (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)",
            alt: image.alt || "",
            width: image.width,
            height: image.height,
          };
        })
        .filter(Boolean);
    }

    generateSrcset(image) {
      if (!image?.src || !image?.width) return "";

      const widths = [165, 360, 533, 720, 940, 1066];
      const srcset = widths
        .filter(w => image.width >= w)
        .map(w => `${this.getImageUrl(image.src, w)} ${w}w`)
        .join(", ");

      return [srcset, `${image.src} ${image.width}w`].filter(Boolean).join(", ");
    }

    getImageUrl(src, width) {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', width);
      return url.toString();
    }

    initPreloadObserver() {
      if (!this.productCard) return;
      if (!("IntersectionObserver" in window)) {
        this.preloadVisibleSwatches();
        return;
      }

      this.preloadObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.preloadVisibleSwatches();
              this.preloadObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: "200px", threshold: 0.01 }
      );

      this.preloadObserver.observe(this.productCard);
    }

    async preloadVisibleSwatches() {
      if (!this.colorMap || !this.optionNodes) return;

      const currentProductPath = this.getCurrentProductPath();
      const renderedSwatches = Array.from(this.optionNodes).filter(
        (node) => node.dataset.value
      );
      const initialSwatches = renderedSwatches.slice(0, 3);
      const deferredSwatches = renderedSwatches.slice(3);

      this.scheduleDeferredSwatches(deferredSwatches, currentProductPath);
      await Promise.all(
        initialSwatches.map((swatch) =>
          this.hydrateSwatch(swatch, currentProductPath)
        )
      );
    }

    scheduleDeferredSwatches(swatches, currentProductPath) {
      if (!swatches.length) return;
      const hydrate = () => {
        this.preloadIdleCallback = null;
        this.preloadIdleCallbackType = null;
        void Promise.all(
          swatches.map((swatch) =>
            this.hydrateSwatch(swatch, currentProductPath)
          )
        );
      };

      if ("requestIdleCallback" in window) {
        this.preloadIdleCallbackType = "idle";
        this.preloadIdleCallback = window.requestIdleCallback(hydrate, {
          timeout: 1500,
        });
      } else {
        this.preloadIdleCallbackType = "timeout";
        this.preloadIdleCallback = window.setTimeout(hydrate, 200);
      }
    }

    async hydrateSwatch(swatch, currentProductPath) {
      const colourValue = swatch?.dataset.value;
      const existing = this.colorMap?.[colourValue];
      if (!existing?.url) return;

      const siblingProductPath = this.getPathFromUrl(existing.url);
      if (siblingProductPath === currentProductPath) return;

      try {
        const loadedSwatch = await this.getSiblingSwatchData(existing.url);
        this.updateSwatchVisual(swatch, loadedSwatch);
      } catch (error) {
        console.error("Failed to preload sibling swatch data:", error);
      }
    }

    updateSwatchVisual(swatch, swatchData) {
      if (!swatch || !swatchData?.src) return;
      let image = swatch.querySelector(".color-swatch__image");
      if (!image) {
        image = document.createElement("img");
        image.className = "color-swatch__image";
        image.alt = "";
        image.loading = "lazy";
        image.decoding = "async";
        image.width = 72;
        image.height = 72;
        swatch.prepend(image);
      }
      image.src = swatchData.src;
      if (swatchData.focalPoint) {
        swatch.style.setProperty(
          "--swatch-focal-point",
          swatchData.focalPoint
        );
      } else {
        swatch.style.removeProperty("--swatch-focal-point");
      }
    }

    observeSelectedColour() {
      if (!this.selected || !this.colorMap || !this.productCard) return;

      const colourData = this.colorMap?.[this.selected?.dataset?.value];
      if (!colourData?.images || colourData.images.length <= 1) return;

      if (!("IntersectionObserver" in window)) {
        this.openInlineCarousel(colourData.images, colourData.url);
        return;
      }

      this.carouselObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            const selected = this.querySelector(this.getSelectedSwatchSelector());
            const selectedData = this.colorMap?.[selected?.dataset.value];
            if (selectedData?.images && selectedData.images.length > 1) {
              this.openInlineCarousel(selectedData.images, selectedData.url);
            }

            this.carouselObserver.unobserve(entry.target);
          });
        },
        { rootMargin: "120px", threshold: 0.01 }
      );

      this.carouselObserver.observe(this.productCard);
    }

    updateCardState(colourData) {
      if (colourData.url) {
        this.currentProductUrl = colourData.url;
      }

      const displayTitle = colourData.style || colourData.title;
      if (displayTitle) {
        const titleAnchor = this.productCard?.querySelector(
          ".product-card__title-link, .product-card__title a"
        );
        if (titleAnchor) {
          titleAnchor.textContent = displayTitle;
          titleAnchor.hidden = false;
        } else {
          this.updateTextElement(".product-card__title", displayTitle);
        }
        this.updateTextElement(".full-width-link .visually-hidden", displayTitle);
      }

      if (Object.prototype.hasOwnProperty.call(colourData, "range")) {
        this.updateTextElement(".product-card__range", colourData.range);
      }

      if (Object.prototype.hasOwnProperty.call(colourData, "color")) {
        this.updateTextElement(".product-card__color", colourData.color);
      }

      if (this.productCard) {
        if (colourData.trackingItem) {
          this.productCard.dataset.gtmProductItem = JSON.stringify(
            colourData.trackingItem
          );
        } else {
          delete this.productCard.dataset.gtmProductItem;
        }
      }

      this.productCard
        ?.querySelectorAll(".product-card__link")
        .forEach((link) => {
          link.href = colourData.url;
        });

      this.productCard
        ?.querySelectorAll("[data-product-quickview]")
        .forEach((button) => {
          button.dataset.productQuickviewUrl = colourData.url;
        });

      this.productCard
        ?.querySelectorAll(".product-card-form input[name='id']")
        .forEach((input) => {
          input.value = colourData.variantId;
        });

      this.productCard
        ?.querySelectorAll(".product-card__atc")
        .forEach((button) => {
          if (button.tagName === "A") {
            button.href = colourData.url;
          }

          if (button.tagName === "BUTTON" && button.name === "add") {
            button.disabled = !colourData.available;
            const text = button.querySelector(".product-card__atc-text");
            if (text) {
              text.textContent = colourData.available
                ? colourData.addToCartText
                : colourData.soldOutText;
            }
          }
        });

      this.updateQuickAddTrigger(colourData);
      this.updateWishlistButton(colourData);

      const price = this.productCard?.querySelector(".f-price");
      if (price) {
        price.classList.toggle(
          "f-price--on-sale",
          Boolean(colourData.compareAtPrice)
        );
        price.classList.toggle("f-price--sold-out", !colourData.available);

        const regular = price.querySelector(
          ".f-price__regular .f-price-item--regular"
        );
        const sale = price.querySelector(".f-price__sale .f-price-item--sale");
        const compare = price.querySelector(
          ".f-price__sale .f-price-item--regular"
        );

        if (regular) regular.textContent = colourData.price;
        if (sale) sale.textContent = colourData.price;
        if (compare && colourData.compareAtPrice) {
          compare.textContent = colourData.compareAtPrice;
        }
      }

      const saleBadge = this.productCard?.querySelector(
        ".product-card__badge .f-badge--sale"
      );
      if (saleBadge) {
        saleBadge.hidden = !Boolean(colourData.compareAtPrice);
      }
    }

    updateTextElement(selector, value) {
      const element = this.productCard?.querySelector(selector);
      if (!element) return;

      const text = value == null ? "" : String(value).trim();
      element.textContent = text;
      element.hidden = !text;
    }

    updateQuickAddTrigger(colourData) {
      this.productCard
        ?.querySelectorAll("[data-quick-add-trigger]")
        .forEach((button) => {
          if (colourData.sku != null) {
            button.dataset.productCode = colourData.sku;
          }

          if (colourData.productPrice != null) {
            button.dataset.productPrice = colourData.productPrice;
          }

          if (colourData.productName || colourData.style || colourData.title) {
            button.dataset.productName =
              colourData.productName || colourData.style || colourData.title;
          }

          if (colourData.hasProductPrice != null) {
            button.dataset.hasProductPrice = String(colourData.hasProductPrice);
          }
        });
    }

    updateWishlistButton(colourData) {
      const wishlistButton = this.productCard?.querySelector(
        ".wishlist-hero-custom-button"
      );
      if (!wishlistButton || !colourData) return;

      const previousId = wishlistButton.getAttribute("data-wlh-id") || "";
      const previousVariantId =
        wishlistButton.getAttribute("data-wlh-variantid") ||
        wishlistButton.getAttribute("data-wlh-variantId") ||
        "";
      const previousLink = wishlistButton.getAttribute("data-wlh-link") || "";

      const nextId =
        colourData.productId != null && colourData.productId !== ""
          ? String(colourData.productId)
          : previousId;
      const urlVariantId = colourData.url
        ? new URL(colourData.url, window.location.origin).searchParams.get(
            "variant"
          )
        : "";
      const nextVariantId =
        colourData.variantId != null && colourData.variantId !== ""
          ? String(colourData.variantId)
          : urlVariantId || previousVariantId;
      const nextLink = colourData.url
        ? this.buildVariantUrl(colourData.url, nextVariantId)
        : previousLink;
      const nextName = String(
        colourData.variantName ||
          colourData.title ||
          colourData.style ||
          colourData.productName ||
          wishlistButton.getAttribute("data-wlh-name") ||
          ""
      ).trim();
      const rawImage =
        colourData.wishlistImage ||
        colourData.images?.[0]?.src ||
        "";
      const nextImage = rawImage
        ? this.getImageUrl(rawImage, 1024)
        : wishlistButton.getAttribute("data-wlh-image") || "";
      const nextPrice =
        colourData.productPrice != null &&
        !Number.isNaN(Number(colourData.productPrice))
          ? String(Number(colourData.productPrice))
          : wishlistButton.getAttribute("data-wlh-price") || "";
      const nextSku =
        colourData.sku ||
        colourData.trackingItem?.full_product_code ||
        wishlistButton.getAttribute("data-wlh-sku") ||
        "";
      const nextVendor =
        colourData.vendor ||
        colourData.trackingItem?.item_brand ||
        wishlistButton.getAttribute("data-wlh-vendor") ||
        "";
      const nextProductType =
        colourData.productType ||
        colourData.trackingItem?.item_category ||
        wishlistButton.getAttribute("data-wlh-product-type") ||
        "";
      const nextColour =
        colourData.color ||
        colourData.colour ||
        colourData.trackingItem?.item_variant ||
        wishlistButton.getAttribute("data-wlh-colour") ||
        "";
      const nextSize =
        colourData.wishlistSize ||
        colourData.size ||
        wishlistButton.getAttribute("data-wlh-size") ||
        "";

      const hasTargetChanged =
        nextId !== previousId ||
        nextVariantId !== previousVariantId ||
        this.getPathFromUrl(nextLink) !== this.getPathFromUrl(previousLink);

      const targetButton =
        hasTargetChanged && wishlistButton.children.length > 0
          ? wishlistButton.cloneNode(false)
          : wishlistButton;

      if (targetButton !== wishlistButton) {
        targetButton.classList.remove("wishlisthero-product-in-wishlist");
      }

      const attributes = {
        "data-wlh-id": nextId,
        "data-wlh-link": nextLink,
        "data-wlh-variantid": nextVariantId,
        "data-wlh-price": nextPrice,
        "data-wlh-name": nextName,
        "data-wlh-image": nextImage,
        "data-wlh-sku": nextSku,
        "data-wlh-vendor": nextVendor,
        "data-wlh-product-type": nextProductType,
        "data-wlh-colour": nextColour,
        "data-wlh-size": nextSize,
      };

      Object.entries(attributes).forEach(([key, value]) => {
        if (value !== "") {
          targetButton.setAttribute(key, value);
        }
      });

      if (targetButton !== wishlistButton) {
        wishlistButton.replaceWith(targetButton);
      }

      if (hasTargetChanged) {
        document.dispatchEvent(
          new CustomEvent("wishlist-hero-add-to-custom-element", {
            detail: targetButton,
          })
        );
      }
    }

    renderColourData(colourData) {
      this.updateCardState(colourData);

      if (colourData.images && colourData.images.length > 1) {
        this.updateStaticImage(colourData.images[0]);
        this.openInlineCarousel(colourData.images, colourData.url);
        return;
      }

      this.closeInlineCarousel();
      this.updateStaticImage(colourData.images?.[0]);
    }

    updateStaticImage(image) {
      if (!image) {
        this.showPlaceholderImage();
        return;
      }

      this.ensureMainImage();
      if (!this.mainImage) return;

      if (image.srcset) this.mainImage.srcset = image.srcset;
      else this.mainImage.removeAttribute("srcset");

      if (image.sizes) this.mainImage.sizes = image.sizes;
      else this.mainImage.removeAttribute("sizes");

      this.mainImage.alt = image.alt || "";
      this.mainImage.src = image.src;

      if (image.width) this.mainImage.width = image.width;
      else this.mainImage.removeAttribute("width");

      if (image.height) this.mainImage.height = image.height;
      else this.mainImage.removeAttribute("height");

      if (this.mainImage.complete) {
        this.mainImage.classList.add("f-img-loaded");
      } else {
        this.mainImage.onload = () => {
          this.mainImage?.classList.add("f-img-loaded");
        };
      }
    }

    ensureMainImage() {
      this.mainImage = this.productCard?.querySelector(
        ".product-card__image--main img"
      );

      if (this.mainImage) return;

      const media = this.productCard?.querySelector("[data-product-card-media]");
      const currentMain =
        media?.querySelector(".product-card__image--main") ||
        media?.querySelector("responsive-image, deferred-media");
      if (!media || !currentMain) return;

      const template = document.createElement("template");
      template.innerHTML = `
        <responsive-image class="f-image product-card__image product-card__image--main" intersecting="true">
          <img class="motion-reduce f-img-loaded" loading="lazy">
        </responsive-image>
      `.trim();

      const responsiveImage = template.content.firstElementChild;
      responsiveImage.style.setProperty(
        "--aspect-ratio",
        media.style.getPropertyValue("--aspect-ratio") || "1"
      );
      currentMain.replaceWith(responsiveImage);
      this.mainImage = responsiveImage.querySelector("img");
    }

    showPlaceholderImage() {
      const media = this.productCard?.querySelector("[data-product-card-media]");
      const currentMain =
        media?.querySelector(".product-card__image--main") ||
        media?.querySelector("responsive-image, deferred-media");
      if (!media || !currentMain) return;

      let placeholder =
        this.placeholderTemplate?.content.firstElementChild?.cloneNode(true);

      if (!placeholder) {
        placeholder = document.createElement("div");
        placeholder.className =
          "product-card__image product-card__image--main color-background-2";
      }

      placeholder.style.setProperty(
        "--aspect-ratio",
        media.style.getPropertyValue("--aspect-ratio") || "1"
      );
      currentMain.replaceWith(placeholder);
      this.mainImage = null;
    }

    openInlineCarousel(images, url) {
      const media = this.productCard?.querySelector("[data-product-card-media]");
      if (!media) return;

      const slider = this.getOrCreateInlineCarousel(media);
      if (!slider || typeof slider.initSlider !== "function") return;

      try {
        if (slider.slider?.instance) {
          slider.destroy();
        }
        slider.initialized = false;
      } catch (error) {
        console.error("Failed to destroy slider:", error);
        slider.initialized = false;
      }

      slider.innerHTML = "";
      slider.numberSlides = images.length;
      slider.classList.remove("flickity-destroyed");

      const existingImg = media.querySelector(".product-card__image--main img");
      const fragment = document.createDocumentFragment();
      images.forEach((image, index) => {
        const slide = document.createElement("a");
        slide.className = "product-card__media-slide";
        slide.href = url;

        let img;
        if (index === 0 && existingImg) {
          img = existingImg.cloneNode(true);
          img.className = "";
        } else {
          img = document.createElement("img");
          img.src = image.src;
          img.alt = image.alt || "";
          if (image.srcset) img.srcset = image.srcset;
          if (image.sizes) img.sizes = image.sizes;
          if (image.width) img.width = image.width;
          if (image.height) img.height = image.height;
          img.setAttribute("loading", "lazy");
          img.setAttribute("fetchpriority", "low");
        }

        slide.appendChild(img);
        fragment.appendChild(slide);
      });

      slider.appendChild(fragment);
      slider.dataset.enableSlider = "true";
      slider.dataset.sliderOptions = JSON.stringify({
        pageDots: false,
        prevNextButtons: true,
        wrapAround: true,
        watchCSS: false,
        setGallerySize: false,
        cellAlign: "center",
        cellSelector: ".product-card__media-slide",
      });
      media.classList.add("has-plp-carousel");
      this.productCard.classList.add("has-active-carousel");
      slider.hidden = false;
      slider.initSlider();
    }

    getOrCreateInlineCarousel(media) {
      let slider = this.productCard?.querySelector(".product-card__media-slider");
      if (slider) return slider;

      slider = document.createElement("flickity-component");
      slider.className =
        "product-card__media-slider flickity-nav-inner flickity-enable flickity-show-controls flickity-button-initialized";
      slider.dataset.enableSlider = "false";
      slider.dataset.sliderColumns = "1";
      slider.dataset.sliderOptions = JSON.stringify({
        pageDots: false,
        prevNextButtons: true,
        wrapAround: true,
        watchCSS: false,
        setGallerySize: false,
        cellAlign: "center",
        cellSelector: ".product-card__media-slide",
      });
      slider.hidden = true;

      const buttonIconColor = getComputedStyle(media)
        .getPropertyValue("--button-icon-color")
        .trim();
      if (buttonIconColor) {
        slider.style.setProperty("--button-icon-color", buttonIconColor);
      }

      const placeholderTemplate = media.querySelector(
        "[data-product-card-placeholder]"
      );
      if (placeholderTemplate) {
        media.insertBefore(slider, placeholderTemplate);
      } else {
        media.appendChild(slider);
      }

      return slider;
    }

    closeInlineCarousel() {
      const media = this.productCard?.querySelector("[data-product-card-media]");
      const slider = this.productCard?.querySelector(".product-card__media-slider");

      if (slider) {
        if (slider.slider?.instance) {
          slider.destroy();
          slider.initialized = false;
        }
        slider.innerHTML = "";
        slider.numberSlides = 0;
        slider.hidden = true;
        slider.dataset.enableSlider = "false";
      }

      media?.classList.remove("has-plp-carousel");
      this.productCard?.classList.remove("has-active-carousel");
    }

    getColorMap() {
      const colorMap = this.nextElementSibling;
      if (!colorMap?.matches?.("[data-color-map]")) return;
      try {
        this.colorMap = JSON.parse(colorMap.textContent);
      } catch (error) {
        console.error("Failed to parse product card colour map:", error);
        this.colorMap = null;
      }
      this.optionNodes.forEach((node) => {
        const sku = this.colorMap?.[node.dataset.value]?.sku;
        if (sku) {
          node.dataset.variantSku = sku;
        }
      });
    }

    getVariantData() {
      const variantJson = this.nextElementSibling;
      if (!variantJson?.matches?.("[data-variant-json]")) return;

      try {
        this.variantData =
          this.variantData || JSON.parse(variantJson.textContent);
      } catch (error) {
        console.error("Failed to parse product card variant data:", error);
        this.variantData = [];
      }
    }
  }

  customElements.define("color-swatch", ColorSwatch);
}
